/* eslint-disable no-unused-vars */
import {
    Row,
    Col,
    Form,
    Space,
    Alert,
    Input,
    Button,
    Select,
    PageHeader,
    Typography,
} from 'antd';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CURRENCIES } from '@/const/currencies.constants';
import { ECountryCodes } from '../../enums/country-code.enum';
import { PhoneNumber } from '../../widgets/PhoneNumber';
import { useAuthStore } from '../../store/auth.store';

const { Option } = Select;
const { Title } = Typography;

interface DriverInformationProps {
    driverData: any;
    setDriverData: (driverData: any) => void;
    nextStep: () => void;
}

const DriverInformation: FC<DriverInformationProps> = ({ setDriverData, nextStep, driverData }) => {
    const { t: ca } = useTranslation('onboarding', { keyPrefix: 'CREATE_ACCOUNT' });
    const { t: tr } = useTranslation('shared', { keyPrefix: 'RESPONSE_ACTIONS' });
    const { t: td } = useTranslation('drivers', { keyPrefix: 'DRIVERS' });

    const user = useAuthStore((state) => state.data);

    const [form] = Form.useForm();
    const [defaultCountryCode, setDefaultCountryCode] = useState<string>(
        /* @ts-ignore */
        `+${ECountryCodes[user.countryCode] || '+593'}`,
    );
    const [error, setError] = useState<any>();

    const onSubmit = async (values: any) => {
        const data = { ...values };
        setError(null);
        setDriverData(data);
        nextStep();
    };

    const fieldProps = {
        label: ca('PHONE'),
        keyCountryCode: 'countryCode',
        keyMobile: 'mobile',
    };

    const handleSelect = (value: string) => {
        form.setFieldValue('countryCode', value);
        setDefaultCountryCode(value);
    };

    useEffect(() => {
        const countryCode = user?.countryCode || '+593';
        handleSelect(countryCode);
    }, [user]);

    return (
        <Space direction="vertical" size={24} style={{ width: '100%' }}>
            <PageHeader
                title={(
                    <Title level={5} style={{ marginBottom: 0 }}>
                        {td('SEND_INVITATION')}
                    </Title>
                )}
                extra={(
                    <Button
                        type="primary"
                        size="small"
                        onClick={() => form.submit()}
                    >
                        {ca('CONTINUE')}
                    </Button>
                )}
            />
            <Form
                form={form}
                onFinish={onSubmit}
                layout="vertical"
                initialValues={{
                    language: 'ES',
                    currency: 'USD',
                    vehicleType: 'BIKE',
                    ...driverData,
                }}
            >
                {
                    error && (
                        <Alert message={error} type="error" />
                    )
                }
                <Row align="top" justify="space-between" gutter={[24, 20]}>
                    <Col xs={24} xxl={12}>
                        <Row align="top" justify="space-between" gutter={[24, 20]}>
                            <Col span={12}>
                                <Form.Item
                                    name="name"
                                    label={ca('FIRST_NAME')}
                                    rules={[{ required: true, message: tr('REQUIRED') }]}
                                >
                                    <Input
                                        placeholder={ca('FIRST_NAME')}
                                    />
                                </Form.Item>
                                <Form.Item
                                    name="email"
                                    label={ca('EMAIL')}
                                    rules={[{ type: 'email', message: tr('REQUIRED'), required: true }]}
                                >
                                    <Input
                                        placeholder={ca('EMAIL')}
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="language"
                                    label={td('LANGUAGE')}
                                    rules={[{ required: true, message: tr('REQUIRED') }]}
                                >
                                    <Select className="signup-select">
                                        <Option value="ES">
                                            {td('SPANISH')}
                                        </Option>
                                        <Option value="EN">
                                            {td('ENGLISH')}
                                        </Option>
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item
                                    name="lastName"
                                    label={ca('LAST_NAME')}
                                    rules={[{ required: true, message: tr('REQUIRED') }]}
                                >
                                    <Input
                                        placeholder={ca('LAST_NAME')}
                                    />
                                </Form.Item>

                                <PhoneNumber
                                    form={form}
                                    fieldProps={fieldProps}
                                    handleSelect={handleSelect}
                                    defaultCountryCode={defaultCountryCode}
                                />

                                <Form.Item
                                    name="currency"
                                    label={td('CURRENCY')}
                                    rules={[{ required: true, message: tr('REQUIRED') }]}
                                >
                                    <Select className="signup-select" options={CURRENCIES} />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Col>
                    <Col xs={24} xxl={12}>
                        <Row align="top" justify="space-between" gutter={[24, 20]}>
                            <Col span={12}>
                                <Form.Item
                                    name="vehicleModelName"
                                    label={td('VEHICLE_MODEL_NAME')}
                                    rules={[{ required: true, message: tr('REQUIRED') }]}
                                >
                                    <Input
                                        placeholder={td('VEHICLE_MODEL_NAME')}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="MCNumber"
                                    label={td('MC_NUMBER')}
                                    rules={[{ required: true, message: tr('REQUIRED') }]}
                                >
                                    <Input
                                        placeholder={td('MC_NUMBER')}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row align="top" justify="space-between" gutter={[24, 20]}>
                            <Col span={12}>
                                <Form.Item
                                    name="vehicleColor"
                                    label={td('VEHICLE_COLOR')}
                                    rules={[{ required: true, message: tr('REQUIRED') }]}
                                >
                                    <Input
                                        placeholder={td('VEHICLE_COLOR')}
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="vehicleType"
                                    label={td('VEHICLE_TYPE')}
                                    rules={[{ required: true, message: tr('REQUIRED') }]}
                                >
                                    <Select className="signup-select">
                                        <Option value="LITE">{td('CAR')}</Option>
                                        <Option value="BIKE">{td('BIKE')}</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="vehicleYear"
                                    label={td('VEHICLE_YEAR')}
                                    rules={[{ required: true, message: tr('REQUIRED') }]}
                                >
                                    <Input
                                        placeholder={td('VEHICLE_YEAR')}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Form>
        </Space>
    );
};

export default DriverInformation;
